import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  LexicalNode,
  PointType,
} from 'lexical';
import { useEffect } from 'react';

export type SlotSelectionSnapshot = {
  text: string;
  start: number;
  end: number;
};

type SelectionTextPluginProps = {
  selectionTextRef?: React.MutableRefObject<SlotSelectionSnapshot | null>;
};

const getPointTextOffset = (
  node: LexicalNode,
  point: PointType,
  currentOffset = 0,
): number | null => {
  if (node.getKey() === point.key) {
    if (point.type === 'text') {
      return currentOffset + point.offset;
    }

    if ($isElementNode(node)) {
      const childOffset = node
        .getChildren()
        .slice(0, point.offset)
        .reduce((total, child) => total + child.getTextContent().length, 0);

      return currentOffset + childOffset;
    }
  }

  if (!$isElementNode(node)) {
    return null;
  }

  let accumulatedOffset = currentOffset;

  for (const child of node.getChildren()) {
    const childOffset = getPointTextOffset(child, point, accumulatedOffset);

    if (childOffset !== null) {
      return childOffset;
    }

    accumulatedOffset += child.getTextContent().length;
  }

  return null;
};

export const SelectionTextPlugin = ({
  selectionTextRef,
}: SelectionTextPluginProps): null => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!selectionTextRef) {
      return;
    }

    const unregister = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection) && !selection.isCollapsed()) {
          const root = $getRoot();
          const points = selection.getStartEndPoints();

          if (points) {
            const [anchor, focus] = points;
            const anchorOffset = getPointTextOffset(root, anchor);
            const focusOffset = getPointTextOffset(root, focus);

            if (anchorOffset !== null && focusOffset !== null) {
              const start = Math.min(anchorOffset, focusOffset);
              const end = Math.max(anchorOffset, focusOffset);

              if (start !== end) {
                selectionTextRef.current = {
                  text: root.getTextContent().slice(start, end),
                  start,
                  end,
                };
                return;
              }
            }
          }
        }

        selectionTextRef.current = null;
      });
    });

    return () => {
      selectionTextRef.current = null;
      unregister();
    };
  }, [editor, selectionTextRef]);

  return null;
};
